import { useState } from 'react';
import { User, MapPin, Briefcase, Clock, ArrowRight, Star, BadgeCheck, EyeOff, X, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { chatService } from '../../api/chat';

const ProfessionalSearchCard = ({ professional, onClick }) => {
    const {
        full_name,
        specialization,
        profile_picture,
        experience_summary,
        aviation_categories,
        verification_status,
        is_profile_hidden,
    } = professional;

    const [toastVisible, setToastVisible] = useState(false);
    const navigate = useNavigate()

    const handleClick = () => {
        if (is_profile_hidden) {
            setToastVisible(true);
            setTimeout(() => setToastVisible(false), 4000);
        }
        onClick();
    };

    const initChat = useMutation({
        mutationFn: (professional_id) => chatService.initializeChatRoom(professional_id),
        onSuccess: () => {
            navigate('/chat')
        }
    })

    const handleNavigateToChat = () => {
        initChat.mutate(professional.id)
    }

    return (
        <div className="relative">
            {/* Hidden-profile toast */}
            {toastVisible && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-800 text-xs font-medium px-3 py-2 rounded-lg shadow-lg whitespace-nowrap animate-fade-in">
                    <EyeOff size={13} className="text-amber-500 flex-shrink-0" />
                    <span>This candidate's profile is hidden — you can contact them via</span>
                    <span className="flex items-center gap-1 text-primary-600 font-semibold">
                        <MessageCircle size={12} /> Chats
                    </span>
                    <button
                        onClick={(e) => { e.stopPropagation(); setToastVisible(false); }}
                        className="ml-1 text-amber-400 hover:text-amber-600"
                    >
                        <X size={12} />
                    </button>
                </div>
            )}

            <div
                className={`group bg-white rounded-2xl p-6 shadow-lg border transition-all duration-300 cursor-pointer relative overflow-hidden
                    ${is_profile_hidden
                        ? 'border-amber-200 hover:border-amber-400 hover:shadow-amber-100 hover:shadow-xl'
                        : 'border-gray-100 hover:border-primary-300 hover:shadow-xl'
                    }`}
            >
                <div className={`absolute top-0 left-0 h-full w-1.5 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300
                    ${is_profile_hidden ? 'bg-amber-400' : 'bg-primary-500'}`}
                />

                <div className="flex items-start space-x-4" onClick={handleClick}>
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-300 overflow-hidden border
                        ${is_profile_hidden
                            ? 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-100'
                            : 'bg-gradient-to-br from-primary-50 to-primary-100 border-primary-100'
                        }`}
                    >
                        {profile_picture ? (
                            <img src={profile_picture} alt={full_name} className="w-full h-full object-cover" />
                        ) : (
                            <User className={`w-8 h-8 ${is_profile_hidden ? 'text-amber-500' : 'text-primary-600'}`} />
                        )}
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                                <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${is_profile_hidden ? 'text-amber-600' : 'text-primary-600'}`}>
                                    {aviation_categories?.pilot_type || 'Aviation Professional'}
                                </p>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="text-xl font-bold text-secondary-900 group-hover:text-primary-700 truncate flex items-center gap-2">
                                        {full_name}
                                        {verification_status && (
                                            <BadgeCheck className="w-5 h-5 text-blue-500 flex-shrink-0" fill="currentColor" color="white" />
                                        )}
                                    </h3>
                                    {is_profile_hidden && (
                                        <span className="flex items-center gap-1 text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex-shrink-0">
                                            <EyeOff size={11} /> Hidden Profile
                                        </span>
                                    )}
                                </div>
                                <p className="text-secondary-600 font-medium text-sm">{specialization}</p>
                            </div>
                            <div className="p-2 rounded-full bg-secondary-50 text-secondary-400 group-hover:bg-primary-50 group-hover:text-primary-500 transition-colors flex-shrink-0 ml-2">
                                <Star className="w-5 h-5" />
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center text-sm text-secondary-500 gap-x-4 gap-y-2 mt-4">
                            {experience_summary?.total_hours && (
                                <span className="flex items-center bg-secondary-50 px-2 py-1 rounded-md">
                                    <Clock size={14} className="mr-1.5 text-primary-500" />
                                    {experience_summary.total_hours} Total Hours
                                </span>
                            )}
                            {experience_summary?.years_of_experience && (
                                <span className="flex items-center bg-secondary-50 px-2 py-1 rounded-md">
                                    <Briefcase size={14} className="mr-1.5 text-primary-500" />
                                    {experience_summary.years_of_experience} Yrs Exp
                                </span>
                            )}
                            {experience_summary?.aircraft_type && (
                                <span className="flex items-center bg-secondary-50 px-2 py-1 rounded-md">
                                    <Star size={14} className="mr-1.5 text-primary-500" />
                                    {experience_summary.aircraft_type}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <span onClick={is_profile_hidden ? handleNavigateToChat : handleClick} className="text-xs font-semibold text-secondary-400 flex items-center">
                        <MapPin size={14} className="mr-1" />
                        {/* contact via Chats */}
                        {is_profile_hidden ? 'Profile hidden — contact via Chats' : 'View Location & Full Profile'}
                    </span>
                    <span onClick={handleClick} className="text-primary-600 font-bold text-sm flex items-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                        View Profile <ArrowRight size={14} className="ml-1.5" />
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ProfessionalSearchCard;
